import React, { useEffect, useId, useState } from "react";
import toast from "react-hot-toast";
import { X, RotateCcw, AlertTriangle } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import Skeleton from "@/components/shared/Skeleton";
import { PlatformService } from "@/services/platformService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";

import { CONFIRM_INPUT_CLASS } from "../constants";
import CodeBlock from "./CodeBlock";
import ErrorNote from "./ErrorNote";
import ResultList from "./ResultList";
import RollbackHazard from "./RollbackHazard";

// Fleet-wide RLS rollback. Two extras over AlembicApplyModal:
// (1) an archived-version picker, (2) a strict "ROLLBACK ALL" confirm gate.
//
// The picker used to offer bare version numbers, which asked somebody to choose
// the row-security policies for every tenant from a list of integers. Each
// option now carries the version's name, and choosing one shows what it does,
// what it leaves deny-all, and — on request — the exact SQL.
//
// `onRefresh` reloads the dashboard after a partial rollback, where some
// databases moved and the modal stays open on the failures.
const RlsFleetRollbackModal = ({
    open,
    rlsCurrent,
    catalog,
    onClose,
    onRefresh,
    onCompleted,
}) => {
    const [versionsLoading, setVersionsLoading] = useState(true);
    const [versionsError, setVersionsError] = useState(null);
    const [availableVersions, setAvailableVersions] = useState([]);
    const [codeCurrent, setCodeCurrent] = useState(null);

    const [selectedVersion, setSelectedVersion] = useState(null);
    const [confirmText, setConfirmText] = useState("");
    const [applying, setApplying] = useState(false);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const confirmId = useId();

    // The SQL for the selected version, fetched only when asked for. It is a
    // few hundred statements, and nobody wants it by default — but before
    // rewriting the row-security policies of every tenant at once, somebody
    // should be able to read exactly what will run.
    const [sqlOpen, setSqlOpen] = useState(false);
    const [sqlLoading, setSqlLoading] = useState(false);
    const [sqlStatements, setSqlStatements] = useState([]);
    const [sqlError, setSqlError] = useState(null);

    // Load the archived versions once the modal opens.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                setVersionsLoading(true);
                setVersionsError(null);
                const data = await PlatformService.fetchRlsVersions();
                if (cancelled) return;
                const versions = data?.versions ?? [];
                const current = data?.current ?? rlsCurrent ?? null;
                setAvailableVersions(versions);
                setCodeCurrent(current);
                // Default selection = highest archived version below current.
                const belowCurrent = versions.filter((v) => current == null || v < current);
                const preferred = belowCurrent.length
                    ? belowCurrent[belowCurrent.length - 1]
                    : versions[0];
                setSelectedVersion(preferred ?? null);
            } catch (err) {
                if (!cancelled) setVersionsError(apiErrorMessageFormat(err));
            } finally {
                if (!cancelled) setVersionsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, rlsCurrent]);

    // Reset transient state on close so a re-open is fresh.
    useEffect(() => {
        if (open) return;
        setConfirmText("");
        setResults(null);
        setError(null);
        setApplying(false);
        setSqlOpen(false);
        setSqlStatements([]);
        setSqlError(null);
    }, [open]);

    // A version's SQL belongs to that version. Changing the selection with the
    // panel open has to re-fetch, not leave the previous version's statements
    // on screen under a new heading.
    useEffect(() => {
        if (!sqlOpen || selectedVersion == null) return;
        let cancelled = false;
        (async () => {
            try {
                setSqlLoading(true);
                setSqlError(null);
                setSqlStatements([]);
                // Rendered against the platform database's app role. Every
                // database gets the same script against its own role, so one is
                // representative — and the password is redacted server-side.
                const data = await PlatformService.previewRls({
                    target: "platform",
                    version: selectedVersion,
                });
                if (!cancelled) setSqlStatements(data?.statements ?? []);
            } catch (err) {
                if (!cancelled) setSqlError(apiErrorMessageFormat(err));
            } finally {
                if (!cancelled) setSqlLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sqlOpen, selectedVersion]);

    const canApply =
        confirmText.trim() === "ROLLBACK ALL" &&
        selectedVersion != null &&
        !applying &&
        !versionsLoading;

    const selectedEntry = catalog?.rlsVersion(selectedVersion);

    // Newest first: a rollback target is chosen by stepping back from where the
    // fleet is now, so the versions nearest to it belong at the top.
    const versionOptions = [...availableVersions]
        .sort((a, b) => b - a)
        .map((v) => {
            const entry = catalog?.rlsVersion(v);
            const name = entry?.title ? ` — ${entry.title}` : "";
            const marker = codeCurrent === v ? " · the version the code is on" : "";
            return { value: String(v), label: `v${v}${name}${marker}` };
        });

    const onApplyClick = async () => {
        if (!canApply) return;
        try {
            setApplying(true);
            setError(null);
            setResults(null);
            const data = await PlatformService.applyRlsVersionToAll(selectedVersion);
            const rows = data?.results ?? [];
            setResults(rows);
            const succeeded = rows.filter((r) => r.success).length;
            const failed = rows.filter((r) => !r.success).length;
            if (failed > 0) {
                toast.error(
                    `${failed} database${failed > 1 ? "s" : ""} failed the rollback. Details below.`
                );
                onRefresh?.();
            } else {
                toast.success(
                    `Rolled ${succeeded} database${
                        succeeded === 1 ? "" : "s"
                    } back to RLS v${selectedVersion}.`
                );
                onCompleted?.();
            }
        } catch (err) {
            setError(apiErrorMessageFormat(err));
        } finally {
            setApplying(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !applying && onClose?.()}>
            <DialogContent className="w-[340px] md:w-[720px] max-h-[85vh] flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <DialogTitle className="text-[15px] font-semibold text-text-primary">
                        Roll back row security across the fleet
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-text-secondary mt-0.5">
                        Applies one archived RLS version to every dedicated tenant
                        database
                    </DialogDescription>
                    <DialogClose
                        disabled={applying}
                        aria-label="Close"
                        className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X size={16} strokeWidth={3} aria-hidden="true" />
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">
                    <div className="rounded-md border border-tone-rose/30 bg-tone-rose-soft p-3 text-xs text-tone-rose">
                        <div className="font-medium mb-1 flex items-center gap-1">
                            <AlertTriangle size={14} aria-hidden="true" />
                            This touches every tenant at once
                        </div>
                        <div>
                            The selected version replaces the row-security policies on{" "}
                            <b>every</b> dedicated tenant database. If that version is
                            broken, every tenant loses data access together. For a single
                            bad tenant use "Roll back row security" on that row instead;
                            this path is for snapping the whole fleet back to a
                            known-good version.
                        </div>
                    </div>

                    {!results && (
                        <>
                            <div>
                                <label
                                    className="block text-xs font-medium text-text-secondary mb-1"
                                    htmlFor={`${confirmId}-version`}
                                >
                                    Version to apply
                                </label>
                                {versionsLoading ? (
                                    <Skeleton className="h-9 w-full" />
                                ) : versionsError ? (
                                    <ErrorNote>{versionsError}</ErrorNote>
                                ) : availableVersions.length === 0 ? (
                                    <div className="text-[12px] text-text-secondary">
                                        The code carries no archived RLS versions, so
                                        there is nothing to roll back to.
                                    </div>
                                ) : (
                                    <>
                                        <Select
                                            id={`${confirmId}-version`}
                                            value={
                                                selectedVersion == null
                                                    ? ""
                                                    : String(selectedVersion)
                                            }
                                            onChange={(v) => setSelectedVersion(Number(v))}
                                            options={versionOptions}
                                            disabled={applying}
                                            placeholder="Pick a version"
                                        />
                                        {codeCurrent != null &&
                                            selectedVersion != null &&
                                            selectedVersion >= codeCurrent && (
                                                <div className="text-[11px] text-tone-amber mt-1">
                                                    v{selectedVersion} is at or above the
                                                    version the code is on (v{codeCurrent}
                                                    ). That is a re-apply, not a rollback
                                                    — the fleet Apply button does the same
                                                    thing with less risk.
                                                </div>
                                            )}
                                    </>
                                )}
                            </div>

                            {/* What the chosen version is, and what it costs.
                                Same catalogue the row drawer reads, so the two
                                cannot describe one version differently. */}
                            {selectedEntry && (
                                <div className="space-y-3">
                                    <div className="text-[12px] text-text-secondary leading-relaxed">
                                        <span className="font-medium text-text-primary">
                                            v{selectedEntry.version} —{" "}
                                            {selectedEntry.title}.{" "}
                                        </span>
                                        {selectedEntry.summary}
                                    </div>
                                    <div className="text-[11px] text-text-secondary">
                                        Polices {selectedEntry.totalTablesPoliced} tables
                                        in {selectedEntry.statementCount} statements.
                                    </div>
                                    <RollbackHazard entry={selectedEntry} />
                                </div>
                            )}

                            {selectedVersion != null && (
                                <div>
                                    <Button
                                        variant="link"
                                        onClick={() => setSqlOpen((v) => !v)}
                                        className="h-auto p-0 text-[11px] text-text-secondary underline underline-offset-2 hover:text-text-primary"
                                    >
                                        {sqlOpen
                                            ? "Hide the SQL"
                                            : `Show the SQL v${selectedVersion} would run`}
                                    </Button>
                                    {sqlOpen && (
                                        <div className="mt-2">
                                            <CodeBlock>
                                                {sqlLoading ? (
                                                    <Skeleton className="h-24 w-full" />
                                                ) : sqlError ? (
                                                    <span className="text-tone-rose">
                                                        {sqlError}
                                                    </span>
                                                ) : sqlStatements.length === 0 ? (
                                                    <span className="text-text-muted">
                                                        No statements returned.
                                                    </span>
                                                ) : (
                                                    sqlStatements.map((stmt, i) => (
                                                        <pre
                                                            key={i}
                                                            className="whitespace-pre-wrap mb-2"
                                                        >
                                                            {stmt};
                                                        </pre>
                                                    ))
                                                )}
                                            </CodeBlock>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div>
                                <label
                                    htmlFor={confirmId}
                                    className="block text-xs text-text-secondary mb-1"
                                >
                                    Type{" "}
                                    <span className="font-mono font-semibold text-text-primary">
                                        ROLLBACK ALL
                                    </span>{" "}
                                    to confirm
                                </label>
                                <Input
                                    id={confirmId}
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    disabled={applying}
                                    className={CONFIRM_INPUT_CLASS}
                                    placeholder="ROLLBACK ALL"
                                    autoComplete="off"
                                />
                            </div>
                        </>
                    )}

                    {results && (
                        <div>
                            <div className="text-xs font-medium text-text-secondary mb-2">
                                Results — target v{selectedVersion}
                            </div>
                            <ResultList
                                results={results}
                                renderDetail={(r) => (
                                    <>
                                        v{r.fromVersion ?? "?"} → v{r.toVersion ?? "?"}
                                        {r.durationMs != null &&
                                            ` · ${(r.durationMs / 1000).toFixed(1)}s`}
                                    </>
                                )}
                            />
                        </div>
                    )}

                    {error && <ErrorNote>{error}</ErrorNote>}
                </div>

                <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
                    <DialogClose
                        disabled={applying}
                        render={
                            <Button variant="outline" type="button" disabled={applying}>
                                {results ? "Close" : "Cancel"}
                            </Button>
                        }
                    />
                    {!results && (
                        <Button
                            onClick={onApplyClick}
                            disabled={!canApply}
                            // `bg-tone-* text-text-invert` is the one solid-tone pair
                            // that clears 4.5:1 in both themes.
                            className="bg-tone-rose text-text-invert hover:bg-tone-rose/90"
                        >
                            <RotateCcw size={14} className="mr-1" aria-hidden="true" />
                            {applying ? "Rolling back…" : "Roll back the fleet"}
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default RlsFleetRollbackModal;
